import { cn } from '@/lib/utils';

import { STEADO_BLADES, STEADO_VIEWBOX } from './steado-geometry';

/**
 * The product's mark: the Steado symbol in white on a brand-red chip, the
 * same lockup the phone app puts on the coach's avatar and its launcher icon.
 *
 * Inline SVG rather than the PNG it used to be. It is three filled blades, so
 * the vector is smaller than the bitmap, it stays crisp on a retina sidebar at
 * any size, and it is one fewer request before the shell can paint.
 *
 * Decorative in every current call site, since the word "Steado" always sits
 * next to it. Pass a label only if that stops being true.
 */
export function SteadoMark({ className }: { readonly className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        // Proportional, not a token: the console's --radius is 14px, so
        // `rounded-md` resolves to 12 and turns a 24px chip into a circle.
        // 35 % is the ratio the phone app's own chips use, and it holds at
        // whatever size a caller asks for.
        'flex size-6 shrink-0 items-center justify-center rounded-[35%] bg-primary',
        className,
      )}
    >
      {/*
       * Wider than the 58 % the hexagon wanted. That mark carried its own
       * margin in the corners of a 1.1667 box; this one is a disc in a square
       * one, so the same fraction reads as a chip with a small logo lost in it.
       */}
      <SteadoSymbol className="w-[66%] text-primary-foreground" />
    </span>
  );
}

/**
 * The bare symbol, in `currentColor` and with no chip around it.
 *
 * Used by the loader, which turns these same three blades rather than drawing
 * a spinner of its own.
 */
export function SteadoSymbol({ className }: { readonly className?: string }) {
  return (
    <svg viewBox={STEADO_VIEWBOX} fill="currentColor" className={className} aria-hidden="true">
      {STEADO_BLADES.map((d, i) => (
        <path key={i} d={d} />
      ))}
    </svg>
  );
}
